/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery, useQueries, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { KYCRequiredModal } from '../components/kyc';
import { volumeService } from '../services/api';
import { Volume, BillingPeriod, NFSCostEstimate } from '../types';
import InsufficientBalanceModal from '../components/wallet/InsufficientBalanceModal';
import VolumeMountPanel, {
  formatVolumeSize,
  formatVolumeUsage,
  volumeAttachments,
  volumeSizeGB,
} from '../components/storage/VolumeMountPanel';
import { useWallet } from '../hooks/useWallet';
import { useSelectedProject } from '../hooks/useSelectedProject';
import { useKYCStatus } from '../hooks/useKYCStatus';
import { validateVolumeName } from '../utils/volumeName';
import {
  Page,
  PageHeader,
  Card,
  Button,
  StatusTag,
  StatTile,
  TableCard,
  EmptyState,
  Skeleton,
  formatINR,
} from '../components/console/ui';
import { DbIc, PlusIc, CloseIc } from '../components/console/icons';

const ALL = 'all';

/** ₹10.5K-style compact amount for stat tiles. */
const compactMoney = (n: number, currency: string) => {
  if (Math.abs(n) < 1000) return formatINR(n, currency);
  const sym = currency === 'USD' ? '$' : '₹';
  const v = n / 1000;
  return `${sym}${v >= 100 ? v.toFixed(0) : v.toFixed(1).replace(/\.0$/, '')}K`;
};

const isRunningStatus = (s?: string) => (s || '').toLowerCase() === 'running';

const normalizeVolumes = (data: any): Volume[] =>
  Array.isArray(data) ? data : data?.data || data?.storages || [];

const Volumes: React.FC = () => {
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const projectFromUrl = searchParams.get('project');
  const { selectedProjectId: globalProjectId, projects, autoSelectFirstProject } = useSelectedProject();

  // Scope defaults to the URL param, else the project chosen in the top bar.
  // "All projects" fans out one list call per project (the API is project-scoped).
  const [scope, setScope] = useState<string>(projectFromUrl || globalProjectId);
  useEffect(() => {
    setScope(projectFromUrl || globalProjectId);
  }, [projectFromUrl, globalProjectId]);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createProject, setCreateProject] = useState('');
  const [volumeToDelete, setVolumeToDelete] = useState<Volume | null>(null);
  const [mountVolume, setMountVolume] = useState<Volume | null>(null);
  const [showInsufficientBalance, setShowInsufficientBalance] = useState(false);
  const [balanceErrorMessage, setBalanceErrorMessage] = useState('');
  const [showKYCModal, setShowKYCModal] = useState(false);
  const [nameError, setNameError] = useState<string | null>(null);

  const { isKYCVerified } = useKYCStatus();

  // Wallet data for balance check
  const { availableBalance, minimumRequired } = useWallet();

  // Auto-select first project if none selected and no URL param
  useEffect(() => {
    if (!projectFromUrl) {
      autoSelectFirstProject();
    }
  }, [projectFromUrl, autoSelectFirstProject]);

  const [newVolumeData, setNewVolumeData] = useState({
    name: '',
    quota_gb: 50,
    nfs_enabled: true,
    billing_period: 'month' as BillingPeriod,
  });

  const projectName = (id: string) => projects.find((p: any) => p.id === id)?.name || '-';

  const scopedProjectIds: string[] = useMemo(
    () => (scope === ALL ? projects.map((p: any) => p.id as string) : scope ? [scope] : []),
    [scope, projects],
  );

  const volumeQueries = useQueries({
    queries: scopedProjectIds.map((pid) => ({
      queryKey: ['volumes', pid],
      queryFn: () => volumeService.list(pid).then((res) => res.data),
      enabled: !!pid,
    })),
  });

  const isLoading = volumeQueries.some((q) => q.isLoading);
  const isError = volumeQueries.length > 0 && volumeQueries.every((q) => q.isError);
  const volumes: Volume[] = useMemo(
    () =>
      volumeQueries.flatMap((q, i) =>
        normalizeVolumes(q.data).map((v) => ({ ...v, project_id: v.project_id || scopedProjectIds[i] })),
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [volumeQueries.map((q) => q.dataUpdatedAt).join(','), scopedProjectIds],
  );

  const liveVolumes = volumes.filter((v) => v.status !== 'deleted');
  const provisionedGB = liveVolumes.reduce((sum, v) => sum + volumeSizeGB(v), 0);
  const hasUnlimited = liveVolumes.some((v) => volumeSizeGB(v) === 0);
  const activeCount = liveVolumes.filter((v) => v.status === 'active').length;
  const largest = [...liveVolumes].sort((a, b) => volumeSizeGB(b) - volumeSizeGB(a))[0];

  // Derived fields from newer backends (attached_to, cost_this_month, used_bytes).
  // Each tile falls back to the older summary when the field is absent.
  const hasAttachments = liveVolumes.some((v) => volumeAttachments(v) !== undefined);
  const hasCost = liveVolumes.some((v) => typeof v.cost_this_month === 'number');
  const costCurrency = liveVolumes.find((v) => v.cost_currency)?.cost_currency || 'INR';
  const costThisMonth = liveVolumes
    .filter((v) => (v.cost_currency || 'INR') === costCurrency)
    .reduce((sum, v) => sum + (v.cost_this_month || 0), 0);
  const attachedRunning = liveVolumes.filter((v) => (volumeAttachments(v) || []).some((a) => isRunningStatus(a.status)));
  const runningPods = new Set(
    liveVolumes.flatMap((v) => (volumeAttachments(v) || []).filter((a) => isRunningStatus(a.status)).map((a) => a.pod_id)),
  ).size;
  // Tip names the largest volume no pod mounts - the likeliest cost to cut.
  const unattached = hasAttachments
    ? [...liveVolumes]
        .filter((v) => v.status === 'active' && volumeAttachments(v)?.length === 0)
        .sort((a, b) => volumeSizeGB(b) - volumeSizeGB(a))[0]
    : undefined;

  // Live cost estimate from the backend. Re-fetches when size or period changes.
  // Only active while the create modal is open and a quota is set, to avoid noise.
  const { data: costEstimate, isFetching: isEstimating } = useQuery<NFSCostEstimate>({
    queryKey: ['volume-estimate', createProject, newVolumeData.quota_gb, newVolumeData.billing_period],
    queryFn: () =>
      volumeService
        .estimateCost(createProject, {
          size_gb: newVolumeData.quota_gb,
          billing_period: newVolumeData.billing_period,
        })
        .then((res) => res.data as NFSCostEstimate),
    enabled: showCreateModal && !!createProject && newVolumeData.quota_gb > 0,
    staleTime: 30_000,
  });

  const createMutation = useMutation({
    mutationFn: () =>
      volumeService.create(createProject, {
        name: newVolumeData.name.trim(),
        quota_gb: newVolumeData.quota_gb,
        nfs_enabled: newVolumeData.nfs_enabled,
        billing_period: newVolumeData.billing_period,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['volumes', createProject] });
      setShowCreateModal(false);
      setNewVolumeData({
        name: '',
        quota_gb: 50,
        nfs_enabled: true,
        billing_period: 'month',
      });
      setNameError(null);
      toast.success('Volume created successfully');
    },
    onError: (error: any) => {
      // Check for insufficient balance error (HTTP 402)
      if (error.response?.status === 402 || error.response?.data?.error === 'insufficient_balance') {
        setBalanceErrorMessage(error.response?.data?.message || 'Insufficient wallet balance to create this resource.');
        setShowInsufficientBalance(true);
      } else {
        toast.error(error.response?.data?.error || 'Failed to create volume');
      }
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (v: Volume) => volumeService.delete(v.id, v.project_id),
    onSuccess: (_res, v) => {
      queryClient.invalidateQueries({ queryKey: ['volumes', v.project_id] });
      toast.success('Volume deleted successfully');
      setVolumeToDelete(null);
      setMountVolume(null);
    },
    onError: (error: any) => {
      const message = error.response?.data?.error || 'Failed to delete volume';
      toast.error(message);
    },
  });

  const confirmDelete = () => {
    if (volumeToDelete) {
      deleteMutation.mutate(volumeToDelete);
    }
  };

  const handleOpenCreateModal = () => {
    const target = scope && scope !== ALL ? scope : globalProjectId || projects[0]?.id || '';
    if (!target) {
      toast.error('Please create a project first');
      return;
    }
    if (!isKYCVerified) {
      setShowKYCModal(true);
      return;
    }
    setCreateProject(target);
    setNameError(null);
    setShowCreateModal(true);
  };

  const scopeLabel = scope === ALL ? 'Showing all projects.' : scope ? `Showing ${projectName(scope)}.` : '';

  return (
    <Page>
      <div className="flex flex-col gap-1">
        <span className="ps-eyebrow">QuickPods</span>
        <PageHeader
          title="NFS volumes"
          subtitle={`Persistent storage for QuickPods GPU Pods. Not available on TrainPods instances. ${scopeLabel}`}
          actions={
            <>
              {!projectFromUrl && (
                <label className="flex items-center gap-2 text-sm text-ps-muted">
                  <span className="sr-only">Project</span>
                  <select value={scope || ''} onChange={(e) => setScope(e.target.value)} className="ps-input w-auto min-w-[170px]">
                    {!scope && <option value="">Select a project…</option>}
                    <option value={ALL}>All projects</option>
                    {projects.map((project: any) => (
                      <option key={project.id} value={project.id}>
                        {project.name}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              <Button onClick={handleOpenCreateModal}>
                <PlusIc className="w-4 h-4" />
                New volume
              </Button>
            </>
          }
        />
      </div>

      {scopedProjectIds.length === 0 ? (
        <EmptyState
          icon={<DbIc />}
          title="Select a project"
          body="Choose a project in the top bar to view and manage its NFS volumes."
        />
      ) : isLoading ? (
        <>
          <div className="grid gap-3.5 grid-cols-[repeat(auto-fit,minmax(min(100%,200px),1fr))]">
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-[92px]" />
            ))}
          </div>
          <Skeleton className="h-48 w-full" />
        </>
      ) : isError ? (
        <EmptyState icon={<DbIc />} title="Couldn’t load volumes" body="The storage service didn’t respond. Try again shortly." />
      ) : liveVolumes.length === 0 ? (
        <EmptyState
          icon={<DbIc />}
          title="No volumes yet"
          body="Create persistent NFS storage and mount it into your GPU Pods."
          actions={<Button onClick={handleOpenCreateModal}>New volume</Button>}
        />
      ) : (
        <>
          <div className="grid gap-3.5 grid-cols-[repeat(auto-fit,minmax(min(100%,200px),1fr))]">
            <StatTile
              label="NFS provisioned"
              value={`${provisionedGB.toFixed(0)} GB${hasUnlimited ? '+' : ''}`}
              sub={`${liveVolumes.length} volume${liveVolumes.length === 1 ? '' : 's'}${hasUnlimited ? ' · some unlimited' : ''}`}
            />
            {hasCost ? (
              <StatTile
                label="Cost this month"
                value={compactMoney(costThisMonth, costCurrency)}
                sub="Month to date · incl. GST"
              />
            ) : (
              <StatTile label="Active" value={`${activeCount} of ${liveVolumes.length}`} sub="Ready to mount" />
            )}
            {hasAttachments ? (
              <StatTile
                label="Attached to running pods"
                value={`${attachedRunning.length} of ${liveVolumes.length}`}
                sub={`${runningPods} pod${runningPods === 1 ? '' : 's'} running`}
              />
            ) : (
              <StatTile
                label="Largest volume"
                value={largest ? formatVolumeSize(largest) : '-'}
                sub={largest?.name}
              />
            )}
            <div className="ps-card p-[18px] flex flex-col gap-1.5 bg-ps-warn-bg border-ps-warn/30">
              <span className="text-[13px] text-ps-warn">Tip</span>
              <span className="text-sm text-ps-text leading-snug">
                {unattached ? (
                  <>
                    <button
                      type="button"
                      onClick={() => setMountVolume(unattached)}
                      className="font-medium text-ps-text hover:text-ps-accent"
                    >
                      {unattached.name}
                    </button>{' '}
                    ({formatVolumeSize(unattached)}) isn’t attached to any pod. Shrink or delete it to cut cost.
                  </>
                ) : (
                  'Volumes bill by quota, not usage. Delete ones you no longer mount to cut cost.'
                )}
              </span>
            </div>
          </div>

          <TableCard>
            <thead>
              <tr>
                <th>Volume</th>
                <th>Project</th>
                <th>Size</th>
                <th>{hasAttachments ? 'Attached to' : 'Billing'}</th>
                {hasCost && <th>This month</th>}
                <th>Status</th>
                <th>Created</th>
                <th>
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {liveVolumes.map((volume) => (
                <tr key={volume.id}>
                  <td>
                    <button
                      type="button"
                      onClick={() => setMountVolume(volume)}
                      className="text-ps-text font-medium hover:text-ps-accent text-left"
                    >
                      {volume.name}
                    </button>
                  </td>
                  <td className="text-ps-muted">{projectName(volume.project_id)}</td>
                  <td>
                    <div>{formatVolumeSize(volume)}</div>
                    {formatVolumeUsage(volume) !== undefined && (
                      <div className="text-xs text-ps-subtle">{formatVolumeUsage(volume)} used</div>
                    )}
                  </td>
                  {hasAttachments ? (
                    <td>
                      <AttachedTo volume={volume} />
                    </td>
                  ) : (
                    <td className="capitalize text-ps-muted">{volume.billing_period || '-'}</td>
                  )}
                  {hasCost && (
                    <td className="font-mono text-[13px]">
                      {typeof volume.cost_this_month === 'number'
                        ? formatINR(volume.cost_this_month, volume.cost_currency || 'INR')
                        : '-'}
                    </td>
                  )}
                  <td>
                    <StatusTag status={(volume.status || '').replace('_', ' ')} />
                  </td>
                  <td className="text-ps-muted">
                    {volume.created_at ? format(new Date(volume.created_at), 'd MMM yyyy') : '-'}
                  </td>
                  <td>
                    <div className="flex justify-end gap-2">
                      <Button variant="secondary" size="sm" onClick={() => setMountVolume(volume)}>
                        Mount info
                      </Button>
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => setVolumeToDelete(volume)}
                        disabled={volume.status === 'pending_deletion'}
                      >
                        Delete
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </TableCard>
        </>
      )}

      <VolumeMountPanel
        volume={mountVolume}
        projectName={mountVolume ? projectName(mountVolume.project_id) : undefined}
        onClose={() => setMountVolume(null)}
        onDelete={(v) => setVolumeToDelete(v)}
      />

      {/* Create Modal */}
      {showCreateModal &&
        createPortal(
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
            <div
              role="dialog"
              aria-modal="true"
              aria-labelledby="create-volume-title"
              className="ps-card w-full max-w-md max-h-[calc(100vh-2rem)] overflow-y-auto"
            >
              <div className="flex items-center justify-between px-6 py-4 border-b border-ps-border">
                <h3 id="create-volume-title" className="m-0 text-lg font-medium">
                  New NFS volume
                </h3>
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  aria-label="Close"
                  className="w-10 h-10 grid place-items-center rounded-md text-ps-muted hover:text-ps-text hover:bg-ps-hover"
                >
                  <CloseIc className="w-5 h-5" />
                </button>
              </div>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  const validationError = validateVolumeName(newVolumeData.name);
                  if (validationError) {
                    setNameError(validationError);
                    return;
                  }
                  createMutation.mutate();
                }}
                className="p-6 flex flex-col gap-4"
              >
                <div>
                  <label htmlFor="vol-name" className="ps-label">
                    Volume name
                  </label>
                  <input
                    id="vol-name"
                    type="text"
                    value={newVolumeData.name}
                    onChange={(e) => {
                      const name = e.target.value;
                      setNewVolumeData({ ...newVolumeData, name });
                      setNameError(name.trim() ? validateVolumeName(name) : null);
                    }}
                    className={`ps-input font-mono ${nameError ? 'border-ps-err focus:border-ps-err focus:ring-ps-err' : ''}`}
                    placeholder="my-data-volume"
                    required
                  />
                  {nameError ? (
                    <p className="m-0 text-xs text-ps-err mt-1.5">{nameError}</p>
                  ) : (
                    <p className="m-0 text-xs text-ps-subtle mt-1.5">Letters, numbers, and hyphens only - no dots or spaces</p>
                  )}
                </div>
                <div>
                  <label htmlFor="vol-project" className="ps-label">
                    Project
                  </label>
                  <select
                    id="vol-project"
                    value={createProject}
                    onChange={(e) => setCreateProject(e.target.value)}
                    className="ps-input"
                    disabled={!!projectFromUrl}
                  >
                    {projects.map((project: any) => (
                      <option key={project.id} value={project.id}>
                        {project.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="vol-quota" className="ps-label">
                      Quota
                    </label>
                    <select
                      id="vol-quota"
                      value={newVolumeData.quota_gb}
                      onChange={(e) => setNewVolumeData({ ...newVolumeData, quota_gb: parseInt(e.target.value) })}
                      className="ps-input"
                    >
                      <option value={0}>No limit</option>
                      {[10, 25, 50, 100, 250, 500, 1000].map((size) => (
                        <option key={size} value={size}>
                          {size} GB
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="vol-period" className="ps-label">
                      Billing period
                    </label>
                    <select
                      id="vol-period"
                      value={newVolumeData.billing_period}
                      onChange={(e) =>
                        setNewVolumeData({ ...newVolumeData, billing_period: e.target.value as BillingPeriod })
                      }
                      className="ps-input"
                    >
                      <option value="month">Monthly</option>
                      <option value="quarter">Quarterly</option>
                      <option value="annual">Annual</option>
                    </select>
                  </div>
                </div>
                <p className="m-0 -mt-2 text-xs text-ps-subtle">
                  {costEstimate
                    ? `₹${costEstimate.rate_per_gb_per_hour.toFixed(4)} per GB/hour for NFS storage`
                    : 'NFS storage billed per GB per hour'}
                </p>
                <label htmlFor="nfs_enabled" className="flex items-center gap-2.5 text-sm text-ps-muted min-h-[44px]">
                  <input
                    type="checkbox"
                    id="nfs_enabled"
                    checked={newVolumeData.nfs_enabled}
                    onChange={(e) => setNewVolumeData({ ...newVolumeData, nfs_enabled: e.target.checked })}
                    className="h-4 w-4 rounded border-ps-strong bg-ps-chrome text-ps-accent focus:ring-ps-accent"
                  />
                  Enable NFS access
                </label>

                {/* Estimated Cost - live from backend so rate changes take effect without a redeploy */}
                {newVolumeData.quota_gb > 0 && (
                  <div className="rounded-lg p-4 bg-ps-accent-soft border border-ps-accent/30">
                    <p className="m-0 text-sm font-medium text-ps-accent">Estimated cost</p>
                    {costEstimate ? (
                      <>
                        {/* Cost breakdown - all values from backend, no client math */}
                        <div className="mt-2 flex flex-col gap-1 text-xs">
                          <div className="flex justify-between gap-3 text-ps-text">
                            <span>
                              Subtotal ({newVolumeData.quota_gb} GB × ₹{costEstimate.rate_per_gb_per_hour.toFixed(4)}/GB/hr)
                            </span>
                            <span className="font-mono">₹{costEstimate.period_cost.toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between text-ps-muted">
                            <span>GST ({(costEstimate.gst_rate_percent ?? 18).toFixed(0)}%)</span>
                            <span className="font-mono">
                              + ₹
                              {(
                                costEstimate.gst_amount ??
                                (costEstimate.period_cost * (costEstimate.gst_rate_percent ?? 18)) / 100
                              ).toFixed(2)}
                            </span>
                          </div>
                          <div className="flex justify-between border-t border-ps-accent/30 pt-1 mt-1 text-sm">
                            <span className="text-ps-accent font-medium">Total / {newVolumeData.billing_period}</span>
                            <span className="font-mono text-ps-accent font-semibold">
                              ₹
                              {(
                                costEstimate.period_cost_with_gst ??
                                costEstimate.period_cost * (1 + (costEstimate.gst_rate_percent ?? 18) / 100)
                              ).toFixed(2)}
                            </span>
                          </div>
                        </div>
                        {costEstimate.discount_percent > 0 && (
                          <p className="m-0 text-[11px] text-ps-ok mt-1">
                            Includes {costEstimate.discount_percent.toFixed(0)}% {newVolumeData.billing_period} discount.
                          </p>
                        )}
                        <p className="m-0 text-[11px] text-ps-muted mt-1">
                          This amount is debited upfront on creation. The full {newVolumeData.billing_period} is billed - no
                          refund if you delete early.
                        </p>
                      </>
                    ) : (
                      <p className="m-0 text-sm text-ps-muted mt-1">
                        {isEstimating ? 'Calculating…' : 'Unable to fetch estimate.'}
                      </p>
                    )}
                  </div>
                )}
                <div className="flex gap-3 pt-1">
                  <Button variant="secondary" className="flex-1" onClick={() => setShowCreateModal(false)}>
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1"
                    disabled={createMutation.isPending || !!nameError || !createProject}
                  >
                    {createMutation.isPending ? 'Creating…' : 'Create volume'}
                  </Button>
                </div>
              </form>
            </div>
          </div>,
          document.body,
        )}

      {/* Delete Confirmation Modal */}
      {volumeToDelete &&
        createPortal(
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4">
            <Card className="w-full max-w-md p-6 flex flex-col gap-3" role="alertdialog" aria-labelledby="del-vol-title">
              <h3 id="del-vol-title" className="m-0 text-lg font-medium">
                Delete volume
              </h3>
              <p className="m-0 text-ps-muted text-sm">
                Are you sure you want to delete <span className="font-semibold text-ps-text">“{volumeToDelete.name}”</span>?
              </p>

              {volumeToDelete.status === 'pending_deletion' ? (
                <div className="rounded-lg p-4 bg-ps-warn-bg border border-ps-warn/30">
                  <p className="m-0 text-sm font-medium text-ps-warn">Volume is being deleted</p>
                  <p className="m-0 text-sm text-ps-muted mt-1">
                    This volume is already scheduled for deletion and will be removed after billing is settled.
                  </p>
                </div>
              ) : (
                <>
                  <p className="m-0 text-sm text-ps-muted">
                    This action cannot be undone. All data in this volume will be permanently deleted.
                  </p>
                  <div className="rounded-lg p-3 bg-ps-warn-bg border border-ps-warn/30">
                    <p className="m-0 text-xs text-ps-warn">
                      <span className="font-semibold">Billing notice:</span> No pro-rated refund. You will be billed for the
                      complete {volumeToDelete.billing_period || 'current'} billing cycle even if you delete now.
                    </p>
                  </div>
                </>
              )}

              <div className="flex gap-3 mt-3">
                <Button variant="secondary" className="flex-1" onClick={() => setVolumeToDelete(null)}>
                  Cancel
                </Button>
                <Button
                  variant="danger"
                  className="flex-1"
                  onClick={confirmDelete}
                  disabled={
                    volumeToDelete.status === 'pending_deletion' ||
                    volumeToDelete.status === 'deleted' ||
                    deleteMutation.isPending
                  }
                >
                  {deleteMutation.isPending ? 'Deleting…' : 'Delete volume'}
                </Button>
              </div>
            </Card>
          </div>,
          document.body,
        )}

      {/* Insufficient Balance Modal */}
      <InsufficientBalanceModal
        isOpen={showInsufficientBalance}
        onClose={() => setShowInsufficientBalance(false)}
        message={balanceErrorMessage}
        requiredAmount={minimumRequired}
        currentBalance={availableBalance}
      />

      {/* KYC Required Modal */}
      <KYCRequiredModal isOpen={showKYCModal} onClose={() => setShowKYCModal(false)} />
    </Page>
  );
};

/** "Attached to" cell: first pod linked, "+N more" for the rest. */
const AttachedTo: React.FC<{ volume: Volume }> = ({ volume }) => {
  const pods = volumeAttachments(volume) || [];
  if (pods.length === 0) return <span className="text-ps-muted">Not attached</span>;
  const [first, ...rest] = pods;
  return (
    <div className="flex flex-col">
      <Link to={`/pods/${first.pod_id}`} className="ps-link truncate max-w-[200px]" title={first.pod_name}>
        {first.pod_name || first.pod_id.slice(0, 8)}
      </Link>
      <span className="text-xs text-ps-subtle">
        {rest.length > 0 ? `+${rest.length} more · ` : ''}
        {first.status}
      </span>
    </div>
  );
};

export default Volumes;
